import { Field, Input, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { Add20Regular, Delete20Regular } from '@fluentui/react-icons';
import {
  AzureGroup,
  ClassCourse,
  TimePattern,
  TimePatternSource,
  UserListSorting,
} from '@mnspro-cloud/services';
import { GroupRole, Person } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { orderBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'react-use';
import { Button } from '../../../Button';
import { Flex } from '../../../Flex';
import { Text } from '../../../Text';

import { AzureGroupSelector } from '../../../AzureGroupSelector/AzureGroupSelector';
import { CommunicationMessage, Illustrations, IllustrationSize } from '../../../Communication';
import { ThemeMode, useProviderTheme } from '../../../hooks/useProviderTheme';
import { List } from '../../../List/List';
import { PersonPicker } from '../../../PersonPicker';
import styles from './ClassCourseForm.module.css';
import { ClassCourseTimeForm } from './ClassCourseTimeForm';
import { CourseTeacherList } from './CourseTeacherList/CourseTeacherList';

export interface ClassCourseFormProps {
  classCourse: ClassCourse;
  teachers?: Person[];
  isLoadingTeachers?: boolean;
  groups?: AzureGroup[];
  isLoadingGroups?: boolean;
  isLoadingCourseTeachers?: boolean;
  onChange?(classCourse: ClassCourse): void;
  /**
   * Will be called if a user clicks on the "Add teacher" button.
   * This callback should be used to trigger loading of teachers.
   *
   * @memberof ClassCourseProps
   */
  onLinkGroupClicked?(): void;
  /**
   * Will be called if a user clicks on the "link" azure group button.
   * This callback should be used to trigger loading of groups.
   *
   * @memberof ClassCourseProps
   */
  onAddTeacherClicked?(): void;
  activeTimePattern?: TimePattern;
  preferenceSorting?: UserListSorting;
}

export const ClassCourseForm: React.FC<ClassCourseFormProps> = ({
  classCourse,
  teachers,
  isLoadingTeachers,
  isLoadingCourseTeachers,
  groups,
  isLoadingGroups,
  onChange,
  onLinkGroupClicked,
  onAddTeacherClicked,
  activeTimePattern,
  preferenceSorting,
}) => {
  const { t } = useTranslation();
  const [showPersonPicker, setShowPersonPicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [internalColor, setInternalColor] = useState(classCourse.color);
  const { mode } = useProviderTheme();
  /*
   Setting color must be debounced because the UI starts to lag
   when the user floats over the color range by holding the mouse pressed,
   which causes hundrets of rerenders within milliseconds.
  */
  useDebounce(
    () => {
      handleUpdate({ color: internalColor });
    },
    300,
    [internalColor],
  );

  const handleUpdate = (course: Partial<ClassCourse>) => {
    if (!classCourse) {
      onChange?.({
        id: '',
        courseTeachers: [],
        name: '',
        classes: [],
        ...course,
      });
    } else {
      onChange?.({ ...classCourse, ...course });
    }
  };

  const handleUpdateColor = (color: string) => setInternalColor(color);

  return (
    <>
      <PersonPicker
        open={showPersonPicker}
        persons={
          teachers?.filter((t) => !classCourse.courseTeachers.some((c) => c.id === t.id)) ?? []
        }
        sortMode={preferenceSorting}
        onPersonSelected={(persons) => {
          const person = persons[0];
          handleUpdate({
            courseTeachers: [
              ...classCourse.courseTeachers.filter((c) => c.id !== person.id),
              person,
            ],
          });

          setShowPersonPicker(false);
        }}
        onCancelClick={() => setShowPersonPicker(false)}
      />
      {activeTimePattern && (
        <ClassCourseTimeForm
          open={showTimePicker}
          timepattern={activeTimePattern}
          onCancel={() => setShowTimePicker(false)}
          onAdd={(value) => {
            handleUpdate({
              occurrences: [
                ...(classCourse.occurrences?.filter(
                  (o) => o.slot.name !== value.timeSlot.name || o.weekday !== value.weekDay,
                ) ?? []),
                { slot: value.timeSlot, weekday: value.weekDay },
              ],
            });
            setShowTimePicker(false);
          }}
        />
      )}
      <Flex direction="column">
        <Field label={t('name', { defaultValue: 'Name' })} required className={styles.section}>
          <Input
            appearance="filled-darker"
            maxLength={64}
            onChange={(_, data) => handleUpdate({ name: data?.value })}
            value={classCourse.name}
          />
        </Field>
        <AzureGroupSelector
          className={styles.section}
          groups={groups?.filter((g) => g.id !== classCourse.azureGroup?.id) ?? []}
          expectedRole={[GroupRole.course, GroupRole.workgroup]}
          azureGroup={classCourse.azureGroup}
          onUpdate={(group) =>
            handleUpdate({
              azureGroup: group,
            })
          }
          isLoadingGroups={isLoadingGroups}
          onSelectGroupClicked={() => onLinkGroupClicked?.()}
        />
        <CourseTeacherList
          className={styles.section}
          isLoading={isLoadingCourseTeachers}
          isLoadingPersons={isLoadingTeachers}
          persons={
            teachers?.filter((t) => !classCourse.courseTeachers.some((c) => c.id === t.id)) ?? []
          }
          selectedPersons={classCourse.courseTeachers ?? []}
          title={t('settings:classbook:course:teachers', { defaultValue: 'Kurslehrer:innen' })}
          onAddPersonClicked={() => onAddTeacherClicked?.()}
          onUpdate={(persons) =>
            handleUpdate({
              courseTeachers: persons,
            })
          }
        />
        <Text className={styles.title} weight="bold">
          {t('settings:classbook:course:times', { defaultValue: 'Unterrichtszeiten' })}
        </Text>
        {!activeTimePattern && (
          <div className="emptyWrapper" style={{ marginBottom: '10px' }}>
            <CommunicationMessage
              illustrationSize={IllustrationSize.Small}
              illustration={Illustrations.Empty}
              useDarkIllustration={mode === ThemeMode.Dark}
              headline={t('settings:classbook:course:title_no_timepattern_configured', {
                defaultValue: 'Kein Stundenplanraster konfiguriert.',
              })}
              text={t('settings:classbook:course:description_no_timepattern_configured', {
                defaultValue:
                  'Es wurde noch kein Stundenplanraster konfiguriert. Erstellen Sie dieses zunächst in den allgemeinen Klassenbucheinstellungen.',
              })}
            />
          </div>
        )}
        {activeTimePattern && (
          <>
            {activeTimePattern.source === TimePatternSource.External && (
              <MessageBar intent="warning">
                <MessageBarBody>
                  {t('settings:classbook:course:external_coursedata_alert', {
                    defaultValue:
                      'Die Daten des Kurses wurden aus externer Quelle importiert und können nicht verändert werden.',
                  })}
                </MessageBarBody>
              </MessageBar>
            )}
            {activeTimePattern.source !== TimePatternSource.External && (
              <Button
                appearance="primary"
                icon={<Add20Regular />}
                onClick={() => setShowTimePicker(true)}
              >
                {t('settings:classbook:course:button_add_time', {
                  defaultValue: 'Zeit hinzufügen',
                })}
              </Button>
            )}
            <div className={styles.section}>
              <List
                entries={orderBy(classCourse.occurrences ?? [], [
                  (occ) => moment(undefined, undefined, 'en').day(occ.weekday).isoWeekday(),
                  (occ) => occ.slot.period,
                ])?.map((occ) => ({
                  label: `${t(occ.weekday.toLowerCase())}, ${occ.slot.start}-${occ.slot.end} (${
                    occ.slot.name
                  })`,
                  actions:
                    activeTimePattern.source !== TimePatternSource.External
                      ? [
                          {
                            icon: (
                              <Delete20Regular
                                aria-label={t(
                                  'settings:classbook:course:classcourseform_delete_label',
                                  { defaultValue: 'Eintrag wird aus der Liste entfernt' },
                                )}
                              />
                            ),
                            onClick: () =>
                              handleUpdate({
                                occurrences:
                                  classCourse.occurrences?.filter(
                                    (o) =>
                                      !(o.slot.name === occ.slot.name && o.weekday === occ.weekday),
                                  ) ?? [],
                              }),
                          },
                        ]
                      : [],
                }))}
              />
            </div>
          </>
        )}
        <Field label={t('color', { defaultValue: 'Farbe' })} className={styles.input}>
          <input
            type="color"
            className={styles.colorInput}
            aria-label={t('color_label', {
              defaultValue: 'Öffnet Flyout zur Farbauswahl für den ausgewählten Kurs',
            })}
            readOnly={activeTimePattern?.source === TimePatternSource.External}
            value={classCourse.color}
            onChange={(e) => handleUpdateColor(e.target.value)}
          />
        </Field>
      </Flex>
    </>
  );
};
